import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { Toast } from "@/lib/utils";

/**
 * `JSON.parse` lives INSIDE the schema, not in front of it.
 *
 * `Schema.safeParse(JSON.parse(raw))` looks equivalent and is not: the throw
 * happens while evaluating the argument, before zod ever runs, so the guard
 * guards nothing. That is exactly how this failed — Safari delivered a frame cut
 * mid-string, `JSON.parse` threw inside an EventSource listener where no caller
 * can catch it, and the raw JavaScriptCore message went to `window.onerror` and
 * out to the user as "Lỗi hệ thống: JSON Parse error: Unterminated string".
 *
 * Wrapped this way, malformed text and wrong shape arrive through the same
 * `success: false` channel and neither can throw.
 */
const JsonText = z.string().transform((raw, ctx) => {
	try {
		return JSON.parse(raw) as unknown;
	} catch {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Khung sự kiện không phải JSON hợp lệ",
		});
		return z.NEVER;
	}
});

const DataUpdateFrame = JsonText.pipe(
	z.object({
		type: z.enum(["students", "statistics", "users"]),
		timestamp: z.string(),
		message: z.string(),
	}),
);

/**
 * Live progress of a tuition-fee refresh. Its own event name, because these
 * arrive up to once a second for hours and must not make every `dataUpdate`
 * listener refetch its data.
 *
 * Deliberately lenient beyond the two fields every sender provides: a frame the
 * server extends must not be dropped wholesale, because dropping one freezes the
 * progress bar for a run that is still going. `trigger` falls back rather than
 * failing for the same reason — a new trigger value is not worth losing the
 * frame over.
 */
const HocPhiProgressFrame = JsonText.pipe(
	z.object({
		running: z.boolean(),
		timestamp: z.string(),
		trigger: z.enum(["manual", "cron", "cli"]).optional().catch(undefined),
		cohortPrefix: z.string().optional(),
		processed: z.number().optional(),
		total: z.number().optional(),
		successful: z.number().optional(),
		failed: z.number().optional(),
		paid: z.number().optional(),
		unpaid: z.number().optional(),
	}),
);

// Inferred from the schemas rather than declared alongside them: a hand-written
// interface can drift from the check that actually runs, and then the types say
// a field is present while the parser never verified it.
type DataUpdateNotification = z.infer<typeof DataUpdateFrame>;
export type HocPhiProgressNotification = z.infer<typeof HocPhiProgressFrame>;

interface EventStreamOptions {
	onDataUpdate?: (notification: DataUpdateNotification) => void;
	onHocPhiProgress?: (progress: HocPhiProgressNotification) => void;
}

// Backoff for the one case EventSource will NOT retry on its own (see the error
// handler below): 1s, 2s, 4s, 8s, 16s. After MAX attempts we stop and hand the
// decision to the user rather than reconnecting forever behind their back — a
// stream that has failed five times in ~30s means the API is down, not flaky.
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 5;

// Stable id so the give-up toast replaces itself instead of stacking.
const RECONNECT_TOAST_ID = "sse-reconnect-failed";

// EventSource handles heartbeats (`: ping` comment lines) and ordinary dropped
// connections on its own, so this hook mostly tracks state and surfaces toasts.
// The one thing it cannot do for us is recover from an HTTP error response —
// see the error handler.
export const useEventStream = (options: EventStreamOptions = {}) => {
	const [isConnected, setIsConnected] = useState(false);
	const [connectionError, setConnectionError] = useState<string | null>(null);
	// True once we have exhausted MAX_RECONNECT_ATTEMPTS and stopped trying.
	const [hasGivenUp, setHasGivenUp] = useState(false);
	const [lastUpdate, setLastUpdate] = useState<DataUpdateNotification | null>(null);
	// Bumping this re-runs the effect below, opening a fresh EventSource —
	// the only way `reconnect()` can force a new connection short of a
	// hand-rolled reconnect loop, which EventSource already does for us.
	const [streamKey, setStreamKey] = useState(0);
	const sourceRef = useRef<EventSource | null>(null);
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const retryAttemptRef = useRef(0);

	// Options come in fresh every render; stash them in a ref so the effect
	// below has stable deps and the stream opens exactly once per mount.
	const optionsRef = useRef(options);
	optionsRef.current = options;

	const disconnect = () => {
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current);
			retryTimerRef.current = null;
		}
		sourceRef.current?.close();
		sourceRef.current = null;
		setIsConnected(false);
	};

	// Manual retry also clears the give-up state, so the backoff starts fresh.
	const reconnect = () => {
		retryAttemptRef.current = 0;
		setHasGivenUp(false);
		Toast.dismiss(RECONNECT_TOAST_ID);
		setStreamKey((key) => key + 1);
	};

	useEffect(() => {
		const source = new EventSource("/v1/events");
		sourceRef.current = source;

		source.addEventListener("open", () => {
			setIsConnected(true);
			setConnectionError(null);
			// A live stream resets the budget, so a later unrelated outage gets its
			// own five attempts instead of inheriting an exhausted counter.
			retryAttemptRef.current = 0;
			setHasGivenUp(false);
			Toast.dismiss(RECONNECT_TOAST_ID);
		});

		source.addEventListener("error", () => {
			setIsConnected(false);
			setConnectionError("Mất kết nối cập nhật trực tiếp");

			// EventSource retries by itself ONLY when the connection drops or the
			// network fails — it goes back to CONNECTING and we leave it alone.
			// A non-2xx HTTP response is different: the spec tells the browser to
			// FAIL the connection, and readyState lands on CLOSED for good. That is
			// not hypothetical here — restarting the API makes nginx answer 502 for
			// a moment, which used to kill realtime for the tab until a manual
			// reload. Reopen it ourselves, with backoff.
			if (source.readyState !== EventSource.CLOSED) {
				return;
			}

			if (retryAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
				// Out of attempts. A reload is the honest fix — it re-runs auth and
				// refetches the data this stream exists to keep fresh — but doing it
				// automatically would discard whatever the user has on screen, so the
				// toast stays up until they choose.
				setHasGivenUp(true);
				Toast.persistentError(
					"Mất kết nối cập nhật trực tiếp",
					"Không thể kết nối lại sau nhiều lần thử. Dữ liệu trên trang có thể đã cũ.",
					{ label: "Tải lại trang", onClick: () => window.location.reload() },
					RECONNECT_TOAST_ID,
				);
				return;
			}

			const delay = Math.min(
				RECONNECT_BASE_DELAY_MS * 2 ** retryAttemptRef.current,
				RECONNECT_MAX_DELAY_MS,
			);
			retryAttemptRef.current += 1;
			retryTimerRef.current = setTimeout(() => setStreamKey((key) => key + 1), delay);
		});

		source.addEventListener("connected", () => {
			setIsConnected(true);
			setConnectionError(null);
		});

		source.addEventListener("dataUpdate", (event: MessageEvent) => {
			const frame = DataUpdateFrame.safeParse(event.data);

			if (!frame.success) {
				// Still refetch. The frame's ARRIVAL is the signal that something
				// changed; only its contents were unreadable. Returning silently is
				// the worse failure of the two — the screen keeps showing numbers
				// that are already stale and nobody watching it can tell.
				//
				// `statistics` is the safe guess: it is the only type
				// `useStatisticsEvents` acts on, so a wrong guess costs one refetch
				// and a missed one costs correctness.
				console.error(
					"Bỏ qua khung dataUpdate hỏng:",
					String(event.data).slice(0, 200),
					frame.error.issues,
				);
				optionsRef.current.onDataUpdate?.({
					type: "statistics",
					timestamp: new Date().toISOString(),
					message: "Dữ liệu đã được cập nhật",
				});
				return;
			}

			const notification = frame.data;
			setLastUpdate(notification);

			// KHÔNG hiện toast ở đây. Sự kiện này là broadcast: nó báo rằng NGƯỜI
			// KHÁC vừa đổi dữ liệu, và mọi máy đang mở đều nhận. Mùa tuyển sinh,
			// nhiều bàn tiếp nhận bấm liên tục thì màn hình của người đang xem
			// thống kê ngập toast về những thao tác không phải của họ.
			//
			// Người thực hiện thao tác đã có toast thành công của chính họ, ngay tại
			// chỗ bấm. Còn người đang xem cần biết "số vừa được làm mới", và trang
			// đã nói điều đó bằng chỉ báo mờ trong lúc nạp lại.
			//
			// Toast mất kết nối thì vẫn giữ — đó là chuyện của chính luồng sự kiện,
			// và không biết thì người dùng ngồi nhìn số đứng yên mà tưởng là đúng.
			optionsRef.current.onDataUpdate?.(notification);
		});

		source.addEventListener("hocphiProgress", (event: MessageEvent) => {
			// Never toasted: one toast per second for the length of a run would
			// bury the screen. The progress bar is the whole notification.
			//
			// Bail before parsing when nobody asked for progress. These arrive once
			// a second for hours and only the học phí admin screen listens; the
			// statistics page would otherwise validate thousands of frames per run
			// and discard every one. The previous `?.(JSON.parse(...))` got this for
			// free because an optional call skips its arguments — that was accidental,
			// this is on purpose.
			const onProgress = optionsRef.current.onHocPhiProgress;
			if (!onProgress) return;

			const frame = HocPhiProgressFrame.safeParse(event.data);

			if (!frame.success) {
				// Dropped outright, unlike `dataUpdate` above: this frame carries the
				// progress numbers themselves rather than announcing that something
				// changed, so there is nothing to substitute. The next frame arrives
				// within a second and the bar catches up.
				console.error(
					"Bỏ qua khung hocphiProgress hỏng:",
					String(event.data).slice(0, 200),
					frame.error.issues,
				);
				return;
			}

			onProgress(frame.data);
		});

		return () => {
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current);
				retryTimerRef.current = null;
			}
			source.close();
			sourceRef.current = null;
		};
	}, [streamKey]);

	return {
		isConnected,
		connectionError,
		hasGivenUp,
		lastUpdate,
		disconnect,
		reconnect,
	};
};

/**
 * Khoảng cách tối thiểu giữa hai lần yêu cầu nạp lại, tính bằng ms.
 *
 * Sự kiện `statistics` không đến rời rạc: mỗi hồ sơ được tra học phí lẻ phát một
 * sự kiện (`hocphi-refresh.service`), mỗi lần cập nhật sinh viên phát một sự kiện
 * (`student.controller`). Mùa cao điểm, nhiều bàn tiếp nhận bấm cùng lúc là một
 * dòng sự kiện liên tục — và không hạ tần thì mỗi sự kiện là một lượt nạp cho cả
 * trang lẫn tab đang mở, tức màn hình nhấp nháy không ngừng.
 *
 * Cạnh lên (leading edge): sự kiện ĐẦU TIÊN nạp lại ngay, vì người đang nhìn cần
 * thấy số đổi tức thì. Những sự kiện trong cửa sổ sau đó dồn thành đúng một lượt
 * nạp khi cửa sổ đóng.
 */
const REFRESH_THROTTLE_MS = 2000;

// Hook specifically for statistics updates
export const useStatisticsEvents = (onUpdate?: (notification: DataUpdateNotification) => void) => {
	const [shouldRefresh, setShouldRefresh] = useState(false);
	/** Đã có sự kiện đến nhưng chưa được biến thành một lượt nạp. */
	const pendingRef = useRef(false);
	/** Đang trong cửa sổ hạ tần. Hết hạn thì phần dồn lại được nạp một lần. */
	const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Hàm dựng lại mỗi lần render, nên giữ trong ref để các listener và timer đã
	// đăng ký trước đó vẫn gọi được bản mới nhất — cùng lối với `optionsRef` ở
	// `useEventStream`.
	const flushRef = useRef<() => void>(() => {});
	flushRef.current = () => {
		if (!pendingRef.current) return;

		// Tab đang bị ẩn thì để nguyên cờ pending. Nạp lại cho một trang không ai
		// nhìn chỉ tốn request của server trong đúng lúc nó đang bận nhất, và người
		// dùng quay lại vẫn có số mới nhờ nhánh `visibilitychange` bên dưới.
		if (document.hidden) return;

		pendingRef.current = false;
		setShouldRefresh(true);

		throttleTimerRef.current = setTimeout(() => {
			throttleTimerRef.current = null;
			flushRef.current();
		}, REFRESH_THROTTLE_MS);
	};

	const stream = useEventStream({
		onDataUpdate: (notification) => {
			if (notification.type !== "statistics") return;

			onUpdate?.(notification);
			pendingRef.current = true;

			// Đang trong cửa sổ hạ tần: timer sẽ nạp phần dồn lại, gọi thêm ở đây chỉ
			// tạo ra đúng cái nhấp nháy cần tránh.
			if (throttleTimerRef.current) return;

			flushRef.current();
		},
	});

	useEffect(() => {
		const onVisibilityChange = () => flushRef.current();
		document.addEventListener("visibilitychange", onVisibilityChange);

		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange);
			if (throttleTimerRef.current) {
				clearTimeout(throttleTimerRef.current);
				throttleTimerRef.current = null;
			}
		};
	}, []);

	const clearRefreshFlag = () => setShouldRefresh(false);

	return {
		...stream,
		shouldRefresh,
		clearRefreshFlag,
	};
};

/** Chỉ theo dõi tiến độ đồng bộ học phí. */
export const useHocPhiProgressEvents = (
	onProgress: (progress: HocPhiProgressNotification) => void,
) => {
	return useEventStream({ onHocPhiProgress: onProgress });
};

export default useEventStream;
